import React from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const steps = [
  {
    number: 1,
    title: 'Start a capture',
    description: 'Hit the global shortcut or pick Capture Text from the menu bar.',
    keys: ['⌘', '⇧', '2'],
    image: { src: '/step1.png', alt: 'Ghost Text menu bar menu on macOS with the Capture Text option' },
  },
  {
    number: 2,
    title: 'Select the text',
    description: 'Drag over any text on screen — it is recognized and copied to your clipboard instantly.',
    keys: null,
    drag: true,
    image: { src: '/step2.png', alt: 'Selecting an area of the Mac screen to recognize text with Ghost Text' },
  },
  {
    number: 3,
    title: 'Paste anywhere',
    description: 'Drop it into notes, editors, messengers — any app that takes text.',
    keys: ['⌘', 'V'],
    image: { src: '/step3.png', alt: 'Pasting text recognized by Ghost Text into another Mac app' },
  },
];

const HowItWorks = () => {
  const headerRef = useScrollAnimation();
  const stepsRef = useScrollAnimation();
  const videoRef = useScrollAnimation();

  return (
    <section id="how-it-works" className="relative py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div ref={headerRef} className="text-center mb-16 animate-slide-up">
          <span className="eyebrow mb-5">How it works</span>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-bone mt-4 mb-5">
            Three keys. Any text.
          </h2>
          <p className="text-lg lg:text-xl text-mist max-w-2xl mx-auto">
            If it renders on your screen, Ghost Text can put it on your clipboard.
          </p>
        </div>

        {/* Steps */}
        <div ref={stepsRef} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 animate-slide-up">
          {steps.map((step) => (
            <div key={step.number} className="card-capture p-6 flex flex-col">
              {/* Step number + keys */}
              <div className="flex items-center justify-between mb-6">
                <span className="font-mono text-sm text-phantom/70">
                  {String(step.number).padStart(2, '0')}
                </span>
                {step.keys ? (
                  <span className="flex items-center gap-1.5">
                    {step.keys.map((key) => (
                      <kbd key={key} className="kbd">{key}</kbd>
                    ))}
                  </span>
                ) : (
                  <span className="font-mono text-xs text-mist border border-dashed border-phantom/40 rounded-md px-2.5 py-1.5">
                    drag to select
                  </span>
                )}
              </div>

              {/* Visual */}
              <div className="rounded-xl overflow-hidden border border-phantom/10 mb-6">
                <img
                  src={step.image.src}
                  alt={step.image.alt}
                  loading="lazy"
                  className="w-full h-44 object-cover"
                />
              </div>

              {/* Content */}
              <h3 className="font-display text-xl font-semibold text-bone mb-2">
                {step.title}
              </h3>
              <p className="text-mist leading-relaxed text-sm">
                {step.description}
              </p>
            </div>
          ))}
        </div>

        {/* Demo Video */}
        <div ref={videoRef} className="mt-16 lg:mt-20 animate-slide-up">
          <div className="text-center mb-8">
            <h3 className="font-display text-2xl font-semibold text-bone">
              Watch a full capture
            </h3>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="mac-window">
              <div className="mac-window-bar">
                <span className="mac-dot bg-[#FF5F57]"></span>
                <span className="mac-dot bg-[#FEBC2E]"></span>
                <span className="mac-dot bg-[#28C840]"></span>
                <span className="flex-1 text-center font-mono text-xs text-mist pr-12">
                  demo.mov
                </span>
              </div>
              <video className="w-full h-auto block" controls>
                <source src="/demo.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
